import api from '@/api/_prelevement'
import stockApi from '@/api/_stock-laboratoire'
import nursingApi from '@/api/consultations/_nursing-station'
import CompteRenduLaboratoire from '@/components/laboratoire/compte-rendu'
import { CONTENANTS_DEFAUT, contenant_par_defaut, libelle_contenant, libelle_type_echantillon, etiquettes_html } from '@/utils/etiquettes-echantillons'

const message = (error, fallback) => error && error.response && error.response.data && typeof error.response.data.message === 'string' ? error.response.data.message : fallback

// Les tests unitaires montent parfois cette vue sans le mixin global. Le repli
// conserve strictement la même présentation M/F sans jamais modifier la donnée.
const sexeCourt = (view, value) => {
    if (typeof view.formatSexeCourt === 'function') return view.formatSexeCourt(value)
    const source = value && typeof value === 'object' ? (value.specificatif || value.description || value.libelle || '') : value
    const raw = source == null ? '' : String(source).trim()
    const normalise = raw.toLocaleLowerCase('fr-FR').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    if (['h', 'm', 'homme', 'masculin', 'masculine', 'male'].includes(normalise)) return 'M'
    if (['f', 'femme', 'feminin', 'feminine', 'female'].includes(normalise)) return 'F'
    return raw || '—'
}

export default {
    name: 'SallePrelevement',
    components: { CompteRenduLaboratoire },
    // LAB-002 : poste appelant (infirmier par défaut, laboratoire sur le tableau du laborantin) ; le rôle signé prime côté serveur
    props: { contexte: { type: String, default: 'infirmier' }, integre: { type: Boolean, default: false }, suivi: { type: Boolean, default: false } },   // LAB-UI-2 : integre = dans la carte du circuit
    data() {
        return {
            onglet: this.suivi ? 'done' : 'pending', recherche: '', date: '', page: 1, pages: 1, total: 0,
            lignes: [], compteurs: null, is_loading: false, rafraichissement_discret: false, is_saving: false, erreur: '', erreur_modal: '',
            modal: false, selection: null, modal_clinique: false, selection_clinique: null, confirme: false, sequence: 0, detruit: false,
            modal_depot: false, selection_depot: null, depose_par_id: null, destinataire_labo_id: null, deposants: [], destinataires_labo: [], personnels_chargement: false, depose_at: '', erreur_depot: '', resultat_bulletin_id: 0, resultat_toggler: 0,
            tubes: [], resultat: null, erreur_tubes: '', contenants: CONTENANTS_DEFAUT, sequence_tubes: 0,
            stock_contenants: {}, stock_chargement: false, stock_erreur: '',
            vue: 'patients', lignes_ouvertes: [],   // LAB-002-F : vue Analyses / Patients et lignes dépliées
            timer: null, search_timer: null,
            onglets: [{ id: 'pending', nom: 'À prélever' }, { id: 'done', nom: 'Prélevés' }, { id: 'deposited', nom: 'Échantillons déposés' }, { id: 'results', nom: 'Résultats' }]
        }
    },
    computed: {
        chargement_visible() { return this.is_loading && !this.rafraichissement_discret },
        // LAB-002-F : vue « Analyses » (une rangée par analyse du bulletin) ou « Patients » (une rangée par bulletin)
        rangees() {
            const rangees = []
            this.lignes.forEach((ligne) => {
                if (this.vue === 'analyses' && Array.isArray(ligne.analyses) && ligne.analyses.length) {
                    ligne.analyses.forEach((analyse, index) => rangees.push({ key: ligne.bulletin_id + '-' + (analyse.demande_id || index), ligne: ligne, analyse: analyse }))
                } else {
                    rangees.push({ key: String(ligne.bulletin_id), ligne: ligne, analyse: null })
                }
            })
            return rangees
        }
    },
    mounted() {
        this.charger()
        window.addEventListener('focus', this.actualiser_visible)
        window.addEventListener('clinicaa:prelevement-updated', this.actualiser_visible)
        this.timer = setInterval(this.actualiser_visible, 30000)
        if (window.BroadcastChannel) {
            this.prelevement_channel = new window.BroadcastChannel('clinicaa-prelevements')
            this.prelevement_channel.onmessage = event => { if (event.data === 'updated') this.actualiser_visible() }
        }
    },
    activated() { this.actualiser_visible() },
    beforeDestroy() {
        this.detruit = true
        this.sequence++
        clearTimeout(this.search_timer)
        clearInterval(this.timer)
        this.timer = null
        if (typeof window !== 'undefined') {
            window.removeEventListener('focus', this.actualiser_visible)
            window.removeEventListener('clinicaa:prelevement-updated', this.actualiser_visible)
        }
        if (this.prelevement_channel) this.prelevement_channel.close()
    },
    methods: {
        async charger(discret = false) {
            if (this.detruit) return
            const sequence = ++this.sequence
            const data = { state: this.onglet, page: this.page, search_item: this.recherche.trim(), one_date: this.date || null, mine: this.suivi ? 1 : 0 }
            this.is_loading = true
            this.rafraichissement_discret = discret === true && this.compteurs !== null
            this.erreur = ''
            try {
                const token = await api.access()
                if (this.detruit || sequence !== this.sequence) return
                const result = await api.list(data, token)
                if (this.detruit || sequence !== this.sequence) return
                if (!result || !Array.isArray(result.data) || !result.counts || !this.onglets.every(tab => Number.isInteger(result.counts[tab.id]) && result.counts[tab.id] >= 0)) throw new Error('Réponse invalide')
                if (JSON.stringify(this.lignes) !== JSON.stringify(result.data)) this.lignes = result.data
                this.compteurs = result.counts
                this.total = result.total
                this.pages = Math.max(1, result.last_page)
                // LAB-002-B : référentiel des contenants fourni par la Clinique
                if (Array.isArray(result.contenants) && result.contenants.length) this.contenants = result.contenants
                if (this.page > this.pages) { this.page = this.pages; return this.charger(discret) }
            } catch (error) {
                if (this.detruit || sequence !== this.sequence) return
                if (!this.rafraichissement_discret) { this.lignes = []; this.compteurs = null; this.total = 0 }
                this.erreur = message(error, 'Impossible de charger les prélèvements. Réessayez avec Actualiser.')
            } finally {
                if (!this.detruit && sequence === this.sequence) this.is_loading = false
            }
        },
        actualiser_visible() {
            if (!this.is_loading && !this.is_saving && !this.detruit && !this.modal && !this.modal_depot && (typeof document === 'undefined' || !document.hidden)) this.charger(true)
        },
        choisir_onglet(id) {
            if (this.onglet === id) return
            this.onglet = id
            this.page = 1
            this.charger()
        },
        chercher() {
            clearTimeout(this.search_timer)
            this.search_timer = setTimeout(() => { this.page = 1; this.charger() }, 300)
        },
        filtrer_date() { this.page = 1; this.charger() },
        // LAB-002-F : vues, ligne dépliable (+) comme le tableau des constantes, identité du patient
        choisir_vue(vue) { this.vue = vue === 'analyses' ? 'analyses' : 'patients'; this.lignes_ouvertes = [] },
        est_ouverte(key) { return this.lignes_ouvertes.indexOf(key) !== -1 },
        basculer_details(key) { this.lignes_ouvertes = this.est_ouverte(key) ? this.lignes_ouvertes.filter(item => item !== key) : this.lignes_ouvertes.concat([key]) },
        sexe_age(ligne) { const sexe = sexeCourt(this, ligne && ligne.sexe); return ligne && ligne.age ? sexe + ' / ' + ligne.age : sexe },
        details_bulletin(ligne) {
            const analyses = Array.isArray(ligne.analyses) ? ligne.analyses : [], echantillons = Array.isArray(ligne.echantillons) ? ligne.echantillons : []
            return [
                ['Patient', ligne.patient], ['N° bulletin', ligne.reference], ['Téléphone', ligne.telephone], ['Assurance', ligne.assurance],
                ['Sexe / âge', this.sexe_age(ligne)], ['N° dossier', ligne.patient_code], ['Prescripteur', ligne.medecin_demandeur], ['Renseignement clinique', ligne.renseignement_clinique],
                ['Bulletin du', this.date_heure(ligne.created_at)], ['Validation caisse', ligne.valide_caisse_at ? this.date_heure(ligne.valide_caisse_at) : ''],
                ['Analyses', analyses.map(analyse => analyse.nom).join(', ')],
                ['Prélèvement', ligne.est_preleve === 1 ? this.date_heure(ligne.preleve_at) + (ligne.preleve_par_nom ? ' · ' + ligne.preleve_par_nom : '') : 'À prélever'],
                ['Échantillons', echantillons.map(echantillon => echantillon.code).join(', ')]
            ]
        },
        apercu_clinique(value) {
            const texte = String(value || '').trim().replace(/\s+/g, ' ')
            return texte.length > 120 ? texte.slice(0, 117) + '…' : texte
        },
        ouvrir_clinique(ligne) {
            if (!ligne || !ligne.renseignement_clinique) return
            this.selection_clinique = ligne
            this.modal_clinique = true
        },
        aller_page(page) { if (page >= 1 && page <= this.pages) { this.page = page; this.charger() } },
        echantillons_depot(ligne) { return ligne && Array.isArray(ligne.echantillons) ? ligne.echantillons : [] },
        peut_deposer(ligne) { return this.echantillons_depot(ligne).some(item => item.etat === 'preleve' && !item.depose_at) },
        maintenant_local() {
            const date = new Date(), deux = value => String(value).padStart(2, '0')
            return date.getFullYear() + '-' + deux(date.getMonth() + 1) + '-' + deux(date.getDate()) + 'T' + deux(date.getHours()) + ':' + deux(date.getMinutes())
        },
        ouvrir_depot(ligne) {
            this.selection_depot = ligne
            this.depose_par_id = null
            this.destinataire_labo_id = null
            this.deposants = []
            this.destinataires_labo = []
            this.depose_at = this.maintenant_local()
            this.erreur_depot = ''
            this.modal_depot = true
            if (this.peut_deposer(ligne)) this.charger_personnels_depot()
        },
        async charger_personnels_depot() {
            this.personnels_chargement = true
            try {
                const token = await api.access()
                const result = await api.personnelsDepot(token)
                if (!this.modal_depot) return
                this.deposants = Array.isArray(result.deposants) ? result.deposants : []
                this.destinataires_labo = Array.isArray(result.destinataires) ? result.destinataires : []
                const courant = Number(result.acteur_id)
                this.depose_par_id = this.deposants.some(personne => personne.id === courant) ? courant : null
            } catch (error) {
                this.erreur_depot = message(error, 'Annuaire du personnel indisponible. Réessayez plus tard.')
            } finally { this.personnels_chargement = false }
        },
        async confirmer_depot() {
            if (!this.selection_depot || !this.peut_deposer(this.selection_depot) || this.is_saving) return
            if (!this.depose_par_id || !this.destinataire_labo_id || !this.depose_at) { this.erreur_depot = 'Choisissez le déposant, le destinataire du laboratoire et l’heure du dépôt.'; return }
            this.is_saving = true
            this.erreur_depot = ''
            try {
                const token = await api.access()
                const result = await api.deposer({ bulletin_id: this.selection_depot.bulletin_id, depose_par_id: this.depose_par_id, destinataire_labo_id: this.destinataire_labo_id, depose_at: this.depose_at.replace('T', ' ') }, token)
                if (!result || result.success !== true) throw new Error('Dépôt refusé')
                this.modal_depot = false
                await this.charger()
            } catch (error) {
                this.erreur_depot = message(error, 'Le dépôt n’a pas été enregistré. Actualisez puis réessayez.')
            } finally { this.is_saving = false }
        },
        ouvrir_resultat(ligne) {
            this.resultat_bulletin_id = ligne.bulletin_id
            this.resultat_toggler++
        },
        date_sans_secondes(value) {
            if (!value) return { jour: '—', heure: '' }
            const parsed = new Date(String(value).replace(' ', 'T') + (/Z$|[+-]\d\d:\d\d$/.test(value) ? '' : 'Z'))
            if (Number.isNaN(parsed.getTime())) return { jour: '—', heure: '' }
            return { jour: parsed.toLocaleDateString('fr-FR'), heure: parsed.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) }
        },
        ouvrir(ligne) {
            this.selection = ligne
            this.confirme = false
            this.erreur_modal = ''
            this.erreur_tubes = ''
            this.resultat = null
            this.tubes = this.tubes_initiaux(ligne)
            this.modal = true
            this.charger_stock_contenants()
        },
        async charger_stock_contenants() {
            this.stock_chargement = true
            this.stock_erreur = ''
            this.stock_contenants = {}
            try {
                let stockId
                if (this.contexte === 'laboratoire') {
                    const stock = await stockApi.stockParCode('laboratoire')
                    stockId = stock && stock.id
                } else {
                    const assignments = await nursingApi.myServices()
                    const services = assignments && Array.isArray(assignments.data) ? assignments.data : []
                    const service = services.find(item => item.is_primary && item.stock_id)
                    stockId = service && service.stock_id
                }
                if (!stockId) throw new Error('Armoire indisponible')
                const detail = await stockApi.stock(stockId)
                if (!detail || !Array.isArray(detail.articles)) throw new Error('Stock indisponible')
                const disponibles = {}
                detail.articles.forEach(article => {
                    let attributs = article.attribut || {}
                    if (typeof attributs === 'string') {
                        try { attributs = JSON.parse(attributs) } catch (e) { attributs = {} }
                    }
                    const code = attributs['Code contenant']
                    if (code) disponibles[code] = (disponibles[code] || 0) + (Number(article.quantite) || 0)
                })
                this.stock_contenants = disponibles
            } catch (error) {
                this.stock_erreur = 'Stock de la salle de prélèvement indisponible. Réessayez.'
            } finally {
                this.stock_chargement = false
            }
        },
        stock_contenant(code) {
            return Object.prototype.hasOwnProperty.call(this.stock_contenants, code)
                ? this.stock_contenants[code] : 0
        },
        // LAB-002-E : comme l'ancienne modale, la fenêtre s'ouvre sur une seule carte vide : aucun contenant choisi,
        // aucune analyse cochée. Le découpage proposé par la Clinique sert seulement à savoir si le circuit laboratoire est actif.
        tubes_initiaux(ligne) {
            const proposes = (ligne && Array.isArray(ligne.echantillons_proposes)) ? ligne.echantillons_proposes : []
            this.sequence_tubes = 0
            return proposes.length ? [this.nouveau_tube()] : []
        },
        nouveau_tube() {
            return { key: ++this.sequence_tubes, contenant_code: '', nombre: 1, demande_ids: [] }
        },
        analyses_prelevables() {
            const analyses = (this.selection && Array.isArray(this.selection.analyses)) ? this.selection.analyses : []
            return analyses.filter(analyse => analyse.demande_id && (!analyse.statut || analyse.statut === 'a_prelever'))
        },
        // Analyses proposables dans la carte : celles qui ne sont pas déjà affectées à un autre contenant
        analyses_disponibles(index) {
            const ailleurs = []
            this.tubes.forEach((tube, position) => { if (position !== index) tube.demande_ids.forEach(id => ailleurs.push(id)) })
            return this.analyses_prelevables().filter(analyse => ailleurs.indexOf(analyse.demande_id) === -1)
        },
        analyses_restantes() {
            const affectees = []
            this.tubes.forEach(tube => tube.demande_ids.forEach(id => affectees.push(id)))
            return this.analyses_prelevables().filter(analyse => affectees.indexOf(analyse.demande_id) === -1)
        },
        tout_affecte(index) {
            const disponibles = this.analyses_disponibles(index)
            return disponibles.length > 0 && disponibles.every(analyse => this.tubes[index].demande_ids.indexOf(analyse.demande_id) !== -1)
        },
        basculer_tout(index) {
            this.tubes[index].demande_ids = this.tout_affecte(index) ? [] : this.analyses_disponibles(index).map(analyse => analyse.demande_id)
            this.suggerer_contenant(this.tubes[index])
        },
        ajouter_tube() {
            if (this.tubes.length && !this.analyses_restantes().length) return
            this.tubes.push(this.nouveau_tube())
        },
        retirer_tube(index) {
            this.tubes.splice(index, 1)
        },
        analyse_dans_tube(tube, demande_id) { return tube.demande_ids.indexOf(demande_id) !== -1 },
        basculer_analyse_tube(tube, demande_id) {
            if (this.analyse_dans_tube(tube, demande_id)) {
                tube.demande_ids = tube.demande_ids.filter(id => id !== demande_id)
            } else {
                tube.demande_ids = tube.demande_ids.concat([demande_id])
                this.suggerer_contenant(tube)
            }
        },
        // Tant qu'aucun contenant n'est choisi, la première analyse cochée propose son contenant par défaut du catalogue (modifiable)
        suggerer_contenant(tube) {
            if (tube.contenant_code) return
            const analyse = this.analyses_prelevables().filter(item => tube.demande_ids.indexOf(item.demande_id) !== -1)[0]
            if (!analyse) return
            tube.contenant_code = contenant_par_defaut(analyse.type_echantillon ? analyse.type_echantillon : 'autre', this.contenants, analyse.contenant_code)
        },
        // Le type d'échantillon découle du contenant, sinon des analyses cochées
        type_du_tube(tube) {
            const contenant = this.contenants.filter(item => item.code === tube.contenant_code)[0]
            if (contenant && contenant.type_echantillon) return contenant.type_echantillon
            const analyse = this.analyses_prelevables().filter(item => tube.demande_ids.indexOf(item.demande_id) !== -1 && item.type_echantillon)[0]
            return analyse ? analyse.type_echantillon : 'autre'
        },
        libelle_type(type) { return libelle_type_echantillon(type) },
        libelle_contenant(code) { return libelle_contenant(code, this.contenants) },
        libelle_option(contenant) {
            return contenant.libelle + (contenant.type_echantillon ? ' — ' + libelle_type_echantillon(contenant.type_echantillon) : '')
        },
        tubes_valides() {
            this.erreur_tubes = ''
            for (let index = 0; index < this.tubes.length; index++) {
                const tube = this.tubes[index], nombre = Number(tube.nombre)
                if (!tube.contenant_code) { this.erreur_tubes = 'Choisissez un contenant pour le contenant ' + (index + 1) + '.'; return false }
                if (!tube.demande_ids.length) { this.erreur_tubes = 'Cochez au moins une analyse pour le contenant ' + (index + 1) + ', ou retirez-le.'; return false }
                if (!Number.isInteger(nombre) || nombre < 1 || nombre > 10) { this.erreur_tubes = 'Le nombre de tubes identiques doit être entre 1 et 10.'; return false }
            }
            // Sans contenant proposé (Clinique sans circuit laboratoire), le flux historique reste possible
            const sans_contenant = this.tubes.length ? this.analyses_restantes() : []
            if (sans_contenant.length) {
                this.erreur_tubes = 'Chaque analyse doit être affectée à un contenant : ' + sans_contenant.map(analyse => analyse.nom).join(', ') + '.'
                return false
            }
            return true
        },
        payload_confirmation() {
            const id = this.selection.bulletin_id, echantillons = {}
            echantillons[id] = this.tubes.map(tube => ({
                type_echantillon: this.type_du_tube(tube), contenant_code: tube.contenant_code, nombre: Number(tube.nombre) || 1, demande_ids: tube.demande_ids.slice()
            }))
            return { bulletin_ids: [id], contexte: this.contexte, echantillons: echantillons }
        },
        imprimer_etiquettes(echantillons) {
            if (typeof window === 'undefined' || typeof window.open !== 'function' || !this.selection) return
            const fenetre = window.open('', '_blank', 'width=900,height=700')
            if (!fenetre) return
            fenetre.document.open()
            fenetre.document.write(etiquettes_html({ patient: this.selection.patient, reference: this.selection.reference }, echantillons))
            fenetre.document.close()
            fenetre.focus()
            fenetre.print()
        },
        fermer() {
            this.modal = false
            this.resultat = null
            this.charger()
        },
        async confirmer() {
            if (!this.selection || this.selection.est_preleve === 1 || this.resultat || !this.confirme || this.is_saving) return
            if (!this.tubes_valides()) return
            this.is_saving = true
            this.erreur_modal = ''
            const id = this.selection.bulletin_id
            try {
                const token = await api.access()
                if (this.detruit) return
                const result = await api.confirm(this.payload_confirmation(), token)
                if (this.detruit) return
                if (!result || result.success !== true) throw new Error('Confirmation refusée')
                const echantillons = (result.echantillons && Array.isArray(result.echantillons[id])) ? result.echantillons[id] : []
                // Invalidation only: never broadcast a patient record or an access token.
                if (this.prelevement_channel) this.prelevement_channel.postMessage('updated')
                if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') window.dispatchEvent(new CustomEvent('clinicaa:prelevement-updated'))
                if (echantillons.length) {
                    // LAB-002 : la fenêtre reste ouverte pour imprimer les étiquettes ; la liste se rafraîchit derrière
                    this.resultat = { echantillons: echantillons, origine: result.origine || null, stock_warning: result.stock_warning || '' }
                } else {
                    this.modal = false
                }
                await this.charger()
            } catch (error) {
                if (!this.detruit) this.erreur_modal = message(error, 'Le prélèvement n’a pas été confirmé. Actualisez puis réessayez.')
            } finally {
                if (!this.detruit) this.is_saving = false
            }
        },
        date_heure(value) {
            if (!value) return 'Non renseignée'
            const parsed = new Date(String(value).replace(' ', 'T') + (/Z$|[+-]\d\d:\d\d$/.test(value) ? '' : 'Z'))
            return Number.isNaN(parsed.getTime()) ? 'Non renseignée' : parsed.toLocaleString('fr-FR')
        }
    }
}
